"use client"

import { useMemo } from "react"
import { API_ENDPOINTS } from "@/lib/api"
import type { AtsSearchItemDto, AtsSearchResponseDto } from "@/lib/ats/ats-placement-mapper"
import type { AtsApi } from "@/hooks/ats/use-ats-placements"

export type { AtsSearchItemDto, AtsSearchResponseDto }

// ATS action surface used by the live ATS tab (the shared WorkflowGrid via AtsSubTabContent +
// workflow-grid kebab actions). The legacy read methods (search / fetchSummary / fetchStatusActions)
// and assignUserAsync were removed with the bespoke tab — list reads now come from the unified
// search index via useWorkflowApi, and inline assignee edits go through the report field-update path.
export interface AtsPlacementApi {
  startVerificationAsync: (recordId: string) => Promise<string | null>
  holdAsync: (recordId: string, comments: string) => Promise<void>
  cancelAsync: (recordId: string, comments: string) => Promise<void>
  createOnboardingSessionAsync: (recordId: string) => Promise<string | null>
  createAsync: (atsSource: string, atsPlacementId: number, force?: boolean) => Promise<string>
  resyncAsync: (recordId: string) => Promise<void>
}

export function useAtsPlacementApi(atsApi: AtsApi): AtsPlacementApi {
  return useMemo(() => ({
    startVerificationAsync: async (recordId) => {
      const result = await atsApi.post<{ requestId: string }>(
        `${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${recordId}/start-pre-verification`
      )
      return result?.requestId ?? null
    },
    holdAsync: (recordId, comments) =>
      atsApi.post(`${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${recordId}/hold`, { comments }),
    cancelAsync: (recordId, comments) =>
      atsApi.post(`${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${recordId}/cancel`, { comments }),
    createOnboardingSessionAsync: async (recordId) => {
      const result = await atsApi.post<{ onboardingId: string }>(
        `${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${recordId}/onboarding-session`
      )
      return result?.onboardingId ?? null
    },
    createAsync: async (atsSource, atsPlacementId, force) => {
      if (!Number.isFinite(atsPlacementId) || atsPlacementId <= 0)
        throw new Error(`Invalid atsPlacementId: ${atsPlacementId}`)
      const encodedSource = encodeURIComponent(atsSource)
      const url = force
        ? `${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${encodedSource}/${atsPlacementId}?force=true`
        : `${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${encodedSource}/${atsPlacementId}`
      const { recordId } = await atsApi.post<{ recordId: string }>(url)
      return recordId
    },
    resyncAsync: (recordId) =>
      atsApi.post(`${API_ENDPOINTS.ATS_SERVICE.PLACEMENT_REQUESTS}/${recordId}/resync`),
  }), [atsApi])
}
